import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from '../hooks/use-toast';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../context/AuthContext';

const CheckoutModal = ({ isOpen, onClose, cart, total, onConfirmOrder }) => {
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [distance, setDistance] = useState(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [addressSelected, setAddressSelected] = useState(false); // NEW: Track if address was selected from list
  const [isOpen24Hours, setIsOpen24Hours] = useState(true);
  const [nextOpenTime, setNextOpenTime] = useState(null);
  const [storeStatus, setStoreStatus] = useState({ isOpen: true, message: '' });
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    orderType: 'delivery', // delivery or pickup
    address: '',
    city: 'Liepaja',
    zipCode: '',
    deliveryTime: 'asap',
    scheduledTime: '',
    paymentMethod: 'card', // card or cash
    notes: ''
  });

  // Auto-fill user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      const defaultAddress = user.addresses?.find(addr => addr.is_default) || user.addresses?.[0];
      
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        phone: user.phone || prev.phone,
        email: user.email || prev.email,
        address: defaultAddress?.address || prev.address,
        city: defaultAddress?.city || prev.city,
        zipCode: defaultAddress?.zip_code || prev.zipCode
      }));
    }
  }, [isOpen, user]);

  // Check if restaurant is open
  useEffect(() => {
    const checkRestaurantHours = () => {
      // Get Riga time (GMT+2)
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
      const day = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const currentTime = hours + minutes / 60;
      
      // Monday is closed (day === 1)
      if (day === 1) {
        const translations = {
          lv: `Pirmdiena ir brīvdiena. Nākamā piegāde iespējama no otrdienas plkst. 12:00`,
          en: `Monday is a day off. Next delivery available from Tuesday at 12:00`,
          ru: `Понедельник выходной. Следующая доставка возможна со вторника в 12:00`
        };
        
        // Calculate next Tuesday
        const nextTuesday = new Date(now);
        nextTuesday.setDate(nextTuesday.getDate() + 1); // Move to Tuesday
        nextTuesday.setHours(12, 0, 0, 0);
        
        setStoreStatus({ isOpen: false, message: translations[language] || translations.lv, closeTime: null });
        setNextOpenTime(nextTuesday);
        
        // Set default delivery time with date for Tuesday
        if (formData.deliveryTime === 'asap' || !formData.deliveryTime.includes('.')) {
          const dateStr = `${nextTuesday.getDate().toString().padStart(2, '0')}.${(nextTuesday.getMonth() + 1).toString().padStart(2, '0')}.${nextTuesday.getFullYear()}`;
          setFormData(prev => ({ ...prev, deliveryTime: `${dateStr} 12:00` }));
        }
        return;
      }
      
      // Opening hours: Tue-Thu: 12-21, Fri-Sat: 12-22, Sun: 12-21
      let openTime = 12;
      let closeTime;
      
      if (day === 5 || day === 6) { // Friday or Saturday
        closeTime = 22;
      } else { // Sunday, Tuesday - Thursday
        closeTime = 21;
      }
      
      const isCurrentlyOpen = currentTime >= openTime && currentTime < closeTime;
      
      let message = '';
      let nextOpen = null;
      
      if (isCurrentlyOpen) {
        // Store is open
        const closeHour = Math.floor(closeTime);
        const closeMin = Math.floor((closeTime % 1) * 60);
        const translations = {
          lv: `Veikals atvērts šodien līdz ${closeHour}:${closeMin.toString().padStart(2, '0')}`,
          en: `Store open today until ${closeHour}:${closeMin.toString().padStart(2, '0')}`,
          ru: `Магазин открыт сегодня до ${closeHour}:${closeMin.toString().padStart(2, '0')}`
        };
        message = translations[language] || translations.lv;
      } else {
        // Store is closed
        let nextOpenDay = new Date(now);
        
        // If before opening time today, next open is today
        // If after closing time, next open is tomorrow
        if (currentTime < openTime) {
          // Before opening - opens today at 12:00
          nextOpenDay = new Date(now);
        } else {
          // After closing - opens tomorrow at 12:00  
          nextOpenDay.setDate(nextOpenDay.getDate() + 1);
        }
        
        // Skip Monday if next open day is Monday
        if (nextOpenDay.getDay() === 1) {
          nextOpenDay.setDate(nextOpenDay.getDate() + 1); // Move to Tuesday
        }
        
        const nextDay = nextOpenDay.getDay();
        let nextClose;
        if (nextDay === 5 || nextDay === 6) {
          nextClose = 22;
        } else {
          nextClose = 21;
        }
        
        const dateStr = nextOpenDay.toLocaleDateString('lv-LV', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        
        const translations = {
          lv: `Nākamā ASAP piegāde iespējama no ${dateStr} plkst. 12:00`,
          en: `Next ASAP delivery available from ${dateStr} at 12:00`,
          ru: `Следующая доставка ASAP возможна с ${dateStr} в 12:00`
        };
        message = translations[language] || translations.lv;
        nextOpen = new Date(nextOpenDay);
        nextOpen.setHours(12, 0, 0, 0);
      }
      
      setStoreStatus({ isOpen: isCurrentlyOpen, message, closeTime });
      setNextOpenTime(nextOpen);
      
      // If store is closed, force scheduled delivery
      if (!isCurrentlyOpen && formData.deliveryTime === 'asap') {
        setFormData(prev => ({ ...prev, deliveryTime: '12:00' }));
      }
    };
    
    checkRestaurantHours();
    // Check every minute
    const interval = setInterval(checkRestaurantHours, 60000);
    
    return () => clearInterval(interval);
  }, [language, formData.deliveryTime]);

  // Generate time slots for delivery
  const generateTimeSlots = (day, closeTime) => {
    const slots = [];
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    let startHour = 12;
    let endHour = 21;
    
    if (day === 'today') {
      // Monday is closed - no today slots on Monday
      if (now.getDay() === 1) {
        return [];
      }
      
      // If it's today and before opening (before 12:00), start from 12:00
      // If it's today and after opening, start from next hour
      if (currentHour < 12) {
        startHour = 12;
      } else {
        startHour = currentHour + 1;
      }
      endHour = closeTime || 21;
    } else if (day === 'tomorrow') {
      // Tomorrow always starts from 12:00
      startHour = 12;
      // Check what day tomorrow is
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // If tomorrow is Monday, skip to Tuesday
      if (tomorrow.getDay() === 1) {
        return []; // Return empty - will be handled by "day after tomorrow"
      }
      
      const tomorrowDay = tomorrow.getDay();
      endHour = (tomorrowDay === 5 || tomorrowDay === 6) ? 22 : 21;
    } else if (day === 'dayAfterTomorrow') {
      // Day after tomorrow (used when tomorrow is Monday)
      startHour = 12;
      const dayAfter = new Date(now);
      dayAfter.setDate(dayAfter.getDate() + 2);
      const dayAfterDay = dayAfter.getDay();
      endHour = (dayAfterDay === 5 || dayAfterDay === 6) ? 22 : 21;
    }
    
    // Generate 30-minute slots
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < endHour - 1) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    
    return slots;
  };

  // Helper function to check if tomorrow is Monday
  const isTomorrowMonday = () => {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.getDay() === 1;
  };

  // Helper function to check if today is Monday
  const isTodayMonday = () => {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
    return now.getDay() === 1;
  };


  // Calculate totals based on order type
  const subtotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
  
  let deliveryFee = 0;
  let discount = 0;
  let discountPercent = 0;
  let finalTotal = subtotal;
  
  if (formData.orderType === 'pickup') {
    // Pickup: 10% discount
    discountPercent = 10;
    discount = subtotal * 0.10;
    finalTotal = subtotal - discount;
  } else {
    // Delivery: calculate delivery fee
    if (subtotal >= 35) {
      deliveryFee = 0;
    } else if (subtotal >= 20) {
      deliveryFee = 2.50;
    }
    
    finalTotal = subtotal + deliveryFee;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Handle address autocomplete with debounce
    if (name === 'address') {
      // Reset address selection and distance when user manually edits
      setAddressSelected(false);
      setDistance(null);
      setSelectedCoordinates(null);
      
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      if (value.length >= 2) {
        setIsSearching(true);
        // Wait 400ms after user stops typing before searching
        searchTimeoutRef.current = setTimeout(() => {
          searchAddress(value);
        }, 400);
      } else {
        setShowSuggestions(false);
        setAddressSuggestions([]);
        setIsSearching(false);
      }
    }
  };

  const searchAddress = async (query) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(
        `${BACKEND_URL}/api/geocoding/address/search?query=${encodeURIComponent(query + ', Liepāja')}`,
        { signal: AbortSignal.timeout(5000) }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch addresses');
      }
      
      const data = await response.json();
      
      // Show ALL results like rozedinapoli.lv
      // If no results, add manual entry option
      let suggestions = data.length > 0 ? data : [{
        display_name: `${query}, Liepāja, LV-3401, Latvija`,
        address: {
          road: query,
          city: 'Liepāja',
          postcode: 'LV-3401'
        },
        manual: true,
        lat: '56.5053',
        lon: '21.0107'
      }];
      
      setAddressSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setIsSearching(false);
      
    } catch (error) {
      console.error('Address search error:', error);
      // On error, allow manual entry
      setAddressSuggestions([{
        display_name: `${formData.address}, Liepāja, LV-3401, Latvija`,
        address: {
          road: formData.address,
          city: 'Liepāja',
          postcode: 'LV-3401'
        },
        manual: true,
        lat: '56.5053',
        lon: '21.0107'
      }]);
      setShowSuggestions(true);
      setIsSearching(false);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const selectAddress = (suggestion) => {
    // Get full address from suggestion
    const address = suggestion.address || {};
    let fullAddress = '';
    
    // Build full address string
    if (address.road) {
      fullAddress = address.road;
      if (address.house_number) {
        fullAddress += ' ' + address.house_number;
      }
    } else if (suggestion.display_name) {
      // Fallback to first part of display_name
      fullAddress = suggestion.display_name.split(',')[0];
    }
    
    // For manual entries, skip validation
    const isManual = suggestion.manual === true;
    
    if (!isManual) {
      // Validate address is in Liepaja (more lenient check)
      const postcode = address.postcode || '';
      const isLiepaja = address.city === 'Liepāja' || 
                        address.town === 'Liepāja' || 
                        address.municipality === 'Liepāja' ||
                        address.municipality?.includes('Liepāja') ||
                        postcode.startsWith('LV-34');
      
      if (!isLiepaja) {
        toast({
          title: t('deliveryNotAvailable'),
          description: t('onlyDeliverLiepaja'),
          variant: "destructive"
        });
        return;
      }
    }
    
    setFormData({
      ...formData,
      address: fullAddress,
      city: 'Liepaja',
      zipCode: address.postcode || formData.zipCode
    });
    setShowSuggestions(false);
    setSelectedCoordinates({ lat: parseFloat(suggestion.lat), lon: parseFloat(suggestion.lon) });
    setAddressSelected(true); // Mark that address was selected from list
    
    // Calculate distance ONLY when address is selected from list
    // Using Nominatim coordinates for consistency with address geocoding API
    const restaurantLat = 56.5049875;
    const restaurantLon = 20.9982351;
    
    // Only calculate if we have valid coordinates
    if (suggestion.lat && suggestion.lon) {
      const dist = calculateDistance(
        restaurantLat, 
        restaurantLon, 
        parseFloat(suggestion.lat), 
        parseFloat(suggestion.lon)
      );
      setDistance(dist);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check minimum order for delivery
    if (formData.orderType === 'delivery' && subtotal < 20) {
      const amountNeeded = (20 - subtotal).toFixed(2);
      toast({
        title: t('minOrderWarning').replace('{amount}', amountNeeded),
        variant: "destructive"
      });
      return;
    }
    
    // Validate form
    if (!formData.name || !formData.phone || !formData.email) {
      toast({
        title: t('missingInfo'),
        description: t('fillAllFields'),
        variant: "destructive"
      });
      return;
    }

    // Validate address for delivery orders
    if (formData.orderType === 'delivery' && (!formData.address || !formData.city)) {
      toast({
        title: t('missingInfo'),
        description: "Please fill in delivery address details.",
        variant: "destructive"
      });
      return;
    }
    
    // Ensure city is Liepaja for delivery
    if (formData.orderType === 'delivery' && formData.city.toLowerCase() !== 'liepaja') {
      toast({
        title: t('deliveryNotAvailable'),
        description: t('onlyDeliverLiepajaPickup'),
        variant: "destructive"
      });
      return;
    }
    
    // Require distance validation for delivery (must have selected from autocomplete)
    if (formData.orderType === 'delivery' && distance === null) {
      toast({
        title: t('addressValidationRequired'),
        description: t('selectAddressFromDropdown'),
        variant: "destructive"
      });
      return;
    }

    // If Cash on Delivery, process without Stripe
    if (formData.paymentMethod === 'cash') {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        
        const cashOrderData = {
          origin_url: window.location.origin,
          customer_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
          delivery_address: formData.orderType === 'delivery' ? formData.address : 'Pickup at Liepu iela 9',
          city: formData.city,
          zip_code: formData.zipCode || 'N/A',
          notes: formData.notes,
          cart_items: cart,
          total_amount: finalTotal,
          order_type: formData.orderType,
          delivery_time: formData.deliveryTime
        };
        
        const response = await fetch(`${BACKEND_URL}/api/payments/cash-order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cashOrderData)
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to create cash order');
        }
        
        const data = await response.json();
        
        // Clear cart after successful cash order
        onConfirmOrder();
        
        // Redirect to success page for cash orders
        const deliveryTimeParam = encodeURIComponent(formData.deliveryTime);
        const orderTypeParam = encodeURIComponent(formData.orderType);
        window.location.href = `/order-success?order_id=${data.order_id}&amount=${finalTotal}&payment_method=cash&delivery_time=${deliveryTimeParam}&order_type=${orderTypeParam}`;
        return;
        
      } catch (error) {
        toast({
          title: "Order Error",
          description: error.message || "Failed to create order. Please try again.",
          variant: "destructive"
        });
        return;
      }
    }

    try {
      // Call backend to create Stripe checkout session
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const originUrl = window.location.origin;
      
      const checkoutData = {
        origin_url: originUrl,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        delivery_address: formData.orderType === 'delivery' ? formData.address : 'Pickup at Liepu iela 9',
        city: formData.city,
        zip_code: formData.zipCode || 'N/A',
        notes: formData.notes,
        cart_items: cart,
        total_amount: finalTotal,
        order_type: formData.orderType,
        delivery_time: formData.deliveryTime
      };
      
      const response = await fetch(`${BACKEND_URL}/api/payments/checkout/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      
      // Clear cart before redirecting to Stripe
      onConfirmOrder();
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      toast({
        title: "Payment Error",
        description: error.message || "Failed to initiate payment. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-black">{t('checkout')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Order Type Selection */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
            <Label className="text-black font-semibold text-lg">{t('orderType')} *</Label>
            <div className="flex gap-4">
              <label className="flex-1 cursor-pointer">
                <input
                  type="radio"
                  name="orderType"
                  value="delivery"
                  checked={formData.orderType === 'delivery'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`p-4 border-2 rounded-xl text-center transition-all ${
                  formData.orderType === 'delivery' 
                    ? 'border-black bg-black text-white' 
                    : 'border-gray-300 bg-white text-black hover:border-black'
                }`}>
                  <span className="font-semibold">🚚 {t('deliveryOption')}</span>
                </div>
              </label>
              <label className="flex-1 cursor-pointer">
                <input
                  type="radio"
                  name="orderType"
                  value="pickup"
                  checked={formData.orderType === 'pickup'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`p-4 border-2 rounded-xl text-center transition-all ${
                  formData.orderType === 'pickup' 
                    ? 'border-black bg-black text-white' 
                    : 'border-gray-300 bg-white text-black hover:border-black'
                }`}>
                  <span className="font-semibold">🏪 {t('pickupOption')} (10% off)</span>
                </div>
              </label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-black font-semibold">{t('fullName')} *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('fullNamePlaceholder')}
                className="border-2 border-gray-300 focus:border-black"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-black font-semibold">{t('phoneNumber')} *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('phoneNumberPlaceholder')}
                className="border-2 border-gray-300 focus:border-black"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-black font-semibold">{t('email')} *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('emailPlaceholder')}
                className="border-2 border-gray-300 focus:border-black"
                required
              />
            </div>
          </div>

          {/* Delivery Address - only show if delivery is selected */}
          {formData.orderType === 'delivery' && (
            <>
              <div className="space-y-2 relative">
                <Label htmlFor="address" className="text-black font-semibold">{t('deliveryAddress')} *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  onFocus={() => formData.address.length > 1 && setShowSuggestions(true)}
                  placeholder={t('startTypingAddress')}
                  className="border-2 border-gray-300 focus:border-black"
                  required={formData.orderType === 'delivery'}
                  autoComplete="off"
                />
                
                {/* Loading indicator */}
                {isSearching && (
                  <div className="absolute right-3 top-11 text-gray-400">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                
                {/* Address Suggestions Dropdown */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-[9999] w-full mt-1 bg-white border-2 border-black rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {addressSuggestions.map((suggestion, index) => {
                      const addr = suggestion.address || {};
                      const street = addr.road || '';
                      const houseNumber = addr.house_number || '';
                      const fullAddr = street + (houseNumber ? ' ' + houseNumber : '');
                      
                      return (
                        <div
                          key={index}
                          onClick={() => selectAddress(suggestion)}
                          className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-0"
                        >
                          <div className="font-semibold text-black">
                            {fullAddr || suggestion.display_name.split(',')[0]}
                          </div>
                          <div className="text-sm text-gray-600">
                            {suggestion.display_name.split(',').slice(1, 3).join(',')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Distance Display - ONLY show if address was selected from list */}
                {distance !== null && addressSelected && (
                  <div className="mt-2 p-3 bg-green-50 border-2 border-green-500 rounded-lg">
                    <p className="text-green-800 font-semibold">
                      ✓ {t('distanceFrom')} {distance.toFixed(2)} {t('kmFrom')}
                    </p>
                  </div>
                )}
                
                {/* Warning if address not selected from list */}
                {formData.orderType === 'delivery' && formData.address.length > 0 && !addressSelected && (
                  <div className="mt-2 p-3 bg-yellow-50 border-2 border-yellow-500 rounded-lg">
                    <p className="text-yellow-800 text-sm font-semibold">
                      {t('selectAddressFromDropdown')}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-black font-semibold">{t('city')} *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Liepaja"
                    className="border-2 border-gray-300 focus:border-black"
                    required={formData.orderType === 'delivery'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zipCode" className="text-black font-semibold">{t('zipCode')}</Label>
                  <Input
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    placeholder="LV-3401"
                    className="border-2 border-gray-300 focus:border-black"
                  />
                </div>
              </div>

              {/* Store Status Banner */}
              <div className={`p-4 rounded-xl mt-4 ${storeStatus.isOpen ? 'bg-green-50 border-2 border-green-200' : 'bg-yellow-50 border-2 border-yellow-300'}`}>
                <p className={`text-sm font-semibold ${storeStatus.isOpen ? 'text-green-800' : 'text-yellow-800'}`}>
                  {storeStatus.message}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deliveryTime" className="text-black font-semibold">{t('deliveryTimeLabel')} *</Label>
                <select
                  id="deliveryTime"
                  name="deliveryTime"
                  value={formData.deliveryTime}
                  onChange={handleChange}
                  className="w-full h-12 px-4 mt-2 border-2 border-gray-300 focus:border-black rounded-lg transition-colors bg-white text-black"
                  required
                >
                  {storeStatus.isOpen && (
                    <option value="asap">ASAP (30 min - 1 stunda)</option>
                  )}
                  
                  {/* Today's time slots with date - NOT on Monday */}
                  {(() => {
                    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
                    const currentHour = now.getHours();
                    const todayDay = now.getDay();
                    
                    // Don't show today if it's Monday (closed)
                    if (todayDay === 1) {
                      return null;
                    }
                    
                    // Show today if store is open OR current time is before 12:00
                    const showToday = storeStatus.isOpen || currentHour < 12;
                    
                    if (showToday) {
                      const todaySlots = generateTimeSlots('today', storeStatus.closeTime);
                      if (todaySlots.length > 0) {
                        const formattedTodayLabel = `${now.getFullYear()}. gada ${now.getDate()}. ${now.toLocaleDateString('lv-LV', { month: 'long' })}`;
                        const dateStr = `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()}`;
                        
                        return (
                          <optgroup label={formattedTodayLabel}>
                            {todaySlots.map(time => (
                              <option key={`today-${time}`} value={`${dateStr} ${time}`}>{time}</option>
                            ))}
                          </optgroup>
                        );
                      }
                    }
                    return null;
                  })()}
                  
                  {/* Tomorrow/Tuesday time slots - handle Monday closure */}
                  {(() => {
                    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
                    const todayDay = now.getDay();
                    const tomorrow = new Date(now);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const tomorrowDay = tomorrow.getDay();
                    
                    // If TODAY is Monday, show ONLY Tuesday (skip today completely)
                    if (todayDay === 1) {
                      // Today is Monday - show Tuesday
                      const tuesday = new Date(now);
                      tuesday.setDate(tuesday.getDate() + 1); // Tomorrow is Tuesday
                      
                      const formattedTuesdayLabel = `${tuesday.getFullYear()}. gada ${tuesday.getDate()}. ${tuesday.toLocaleDateString('lv-LV', { month: 'long' })}`;
                      const dateStr = `${tuesday.getDate().toString().padStart(2, '0')}.${(tuesday.getMonth() + 1).toString().padStart(2, '0')}.${tuesday.getFullYear()}`;
                      
                      return (
                        <optgroup label={formattedTuesdayLabel}>
                          {generateTimeSlots('tomorrow').map(time => (
                            <option key={`tuesday-${time}`} value={`${dateStr} ${time}`}>{time}</option>
                          ))}
                        </optgroup>
                      );
                    }
                    
                    // If TOMORROW is Monday (today is Sunday), skip to Tuesday
                    if (tomorrowDay === 1) {
                      // Tomorrow is Monday - show Tuesday instead
                      const tuesday = new Date(now);
                      tuesday.setDate(tuesday.getDate() + 2);
                      
                      const formattedTuesdayLabel = `${tuesday.getFullYear()}. gada ${tuesday.getDate()}. ${tuesday.toLocaleDateString('lv-LV', { month: 'long' })}`;
                      const dateStr = `${tuesday.getDate().toString().padStart(2, '0')}.${(tuesday.getMonth() + 1).toString().padStart(2, '0')}.${tuesday.getFullYear()}`;
                      
                      return (
                        <optgroup label={formattedTuesdayLabel}>
                          {generateTimeSlots('dayAfterTomorrow').map(time => (
                            <option key={`tuesday-${time}`} value={`${dateStr} ${time}`}>{time}</option>
                          ))}
                        </optgroup>
                      );
                    }
                    
                    // Normal case - show tomorrow
                    const formattedTomorrowLabel = `${tomorrow.getFullYear()}. gada ${tomorrow.getDate()}. ${tomorrow.toLocaleDateString('lv-LV', { month: 'long' })}`;
                    const tomorrowDateStr = `${tomorrow.getDate().toString().padStart(2, '0')}.${(tomorrow.getMonth() + 1).toString().padStart(2, '0')}.${tomorrow.getFullYear()}`;
                    
                    return (
                      <optgroup label={formattedTomorrowLabel}>
                        {generateTimeSlots('tomorrow').map(time => (
                          <option key={`tomorrow-${time}`} value={`${tomorrowDateStr} ${time}`}>{time}</option>
                        ))}
                      </optgroup>
                    );
                  })()}
                </select>
              </div>
            </>
          )}

          {/* Pickup Address Display */}
          {formData.orderType === 'pickup' && (
            <>
              <div className="p-4 bg-green-50 border-2 border-green-500 rounded-xl">
                <h4 className="font-bold text-black mb-2">📍 {t('pickupAddress') || 'Pickup Address'}</h4>
                <p className="text-gray-700">Liepu iela 9, Liepāja, Latvija</p>
              </div>

              {/* Store Status Banner for Pickup */}
              <div className={`p-4 rounded-xl ${storeStatus.isOpen ? 'bg-green-50 border-2 border-green-200' : 'bg-yellow-50 border-2 border-yellow-300'}`}>
                <p className={`text-sm font-semibold ${storeStatus.isOpen ? 'text-green-800' : 'text-yellow-800'}`}>
                  {storeStatus.message}
                </p>
              </div>

              {/* Pickup Time Selection */}
              <div className="space-y-2">
                <Label htmlFor="pickupTime" className="text-black font-semibold">{t('pickupTimeLabel') || 'Pickup Time'} *</Label>
                <select
                  id="deliveryTime"
                  name="deliveryTime"
                  value={formData.deliveryTime}
                  onChange={handleChange}
                  className="w-full h-12 px-4 mt-2 border-2 border-gray-300 focus:border-black rounded-lg transition-colors bg-white text-black"
                  required
                >
                  {storeStatus.isOpen && (
                    <option value="asap">ASAP (30 min)</option>
                  )}
                  
                  {/* Today's time slots - NOT on Monday */}
                  {(() => {
                    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
                    const currentHour = now.getHours();
                    const todayDay = now.getDay();
                    
                    if (todayDay === 1) return null;
                    
                    const showToday = storeStatus.isOpen || currentHour < 12;
                    
                    if (showToday) {
                      const todaySlots = generateTimeSlots('today', storeStatus.closeTime);
                      if (todaySlots.length > 0) {
                        const formattedTodayLabel = `${now.getFullYear()}. gada ${now.getDate()}. ${now.toLocaleDateString('lv-LV', { month: 'long' })}`;
                        const dateStr = `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()}`;
                        
                        return (
                          <optgroup label={formattedTodayLabel}>
                            {todaySlots.map(time => (
                              <option key={`pickup-today-${time}`} value={`${dateStr} ${time}`}>{time}</option>
                            ))}
                          </optgroup>
                        );
                      }
                    }
                    return null;
                  })()}
                  
                  {/* Tomorrow/Tuesday time slots for pickup */}
                  {(() => {
                    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Riga' }));
                    const todayDay = now.getDay();
                    const tomorrow = new Date(now);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    const tomorrowDay = tomorrow.getDay();
                    
                    if (todayDay === 1) {
                      const tuesday = new Date(now);
                      tuesday.setDate(tuesday.getDate() + 1);
                      
                      const formattedTuesdayLabel = `${tuesday.getFullYear()}. gada ${tuesday.getDate()}. ${tuesday.toLocaleDateString('lv-LV', { month: 'long' })}`;
                      const dateStr = `${tuesday.getDate().toString().padStart(2, '0')}.${(tuesday.getMonth() + 1).toString().padStart(2, '0')}.${tuesday.getFullYear()}`;
                      
                      return (
                        <optgroup label={formattedTuesdayLabel}>
                          {generateTimeSlots('tomorrow').map(time => (
                            <option key={`pickup-tuesday-${time}`} value={`${dateStr} ${time}`}>{time}</option>
                          ))}
                        </optgroup>
                      );
                    }
                    
                    if (tomorrowDay === 1) {
                      const tuesday = new Date(now);
                      tuesday.setDate(tuesday.getDate() + 2);
                      
                      const formattedTuesdayLabel = `${tuesday.getFullYear()}. gada ${tuesday.getDate()}. ${tuesday.toLocaleDateString('lv-LV', { month: 'long' })}`;
                      const dateStr = `${tuesday.getDate().toString().padStart(2, '0')}.${(tuesday.getMonth() + 1).toString().padStart(2, '0')}.${tuesday.getFullYear()}`;
                      
                      return (
                        <optgroup label={formattedTuesdayLabel}>
                          {generateTimeSlots('dayAfterTomorrow').map(time => (
                            <option key={`pickup-tuesday-${time}`} value={`${dateStr} ${time}`}>{time}</option>
                          ))}
                        </optgroup>
                      );
                    }
                    
                    const formattedTomorrowLabel = `${tomorrow.getFullYear()}. gada ${tomorrow.getDate()}. ${tomorrow.toLocaleDateString('lv-LV', { month: 'long' })}`;
                    const tomorrowDateStr = `${tomorrow.getDate().toString().padStart(2, '0')}.${(tomorrow.getMonth() + 1).toString().padStart(2, '0')}.${tomorrow.getFullYear()}`;
                    
                    return (
                      <optgroup label={formattedTomorrowLabel}>
                        {generateTimeSlots('tomorrow').map(time => (
                          <option key={`pickup-tomorrow-${time}`} value={`${tomorrowDateStr} ${time}`}>{time}</option>
                        ))}
                      </optgroup>
                    );
                  })()}
                </select>
              </div>
            </>
          )}

          {/* Payment Method Selection */}
          <div className="space-y-3 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
            <Label className="text-black font-semibold text-lg">{t('paymentMethod')} *</Label>
            <div className="flex gap-4">
              <label className="flex-1 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="card"
                  checked={formData.paymentMethod === 'card'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`p-4 border-2 rounded-xl text-center transition-all ${
                  formData.paymentMethod === 'card' 
                    ? 'border-black bg-black text-white' 
                    : 'border-gray-300 bg-white text-black hover:border-black'
                }`}>
                  <span className="font-semibold">💳 {t('creditCard')}</span>
                </div>
              </label>
              <label className="flex-1 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={formData.paymentMethod === 'cash'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className={`p-4 border-2 rounded-xl text-center transition-all ${
                  formData.paymentMethod === 'cash' 
                    ? 'border-black bg-black text-white' 
                    : 'border-gray-300 bg-white text-black hover:border-black'
                }`}>
                  <span className="font-semibold">💵 {t('cashOnDelivery')}</span>
                </div>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes" className="text-black font-semibold">{t('specialInstructionsLabel')} {t('optional')}</Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder={t('specialInstructionsPlaceholder')}
              className="border-2 border-gray-300 focus:border-black min-h-[100px]"
            />
          </div>

          <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
            <h3 className="font-bold text-black mb-4 text-lg">{t('orderSummary')}</h3>
            <div className="space-y-2 mb-4">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {item.name} x {item.quantity}
                  </span>
                  <span className="font-semibold text-black">
                    €{(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t-2 border-gray-300 pt-4 space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>{t('subtotal')}</span>
                <span className="font-semibold">€{subtotal.toFixed(2)}</span>
              </div>
              
              {formData.orderType === 'delivery' && deliveryFee > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>{t('deliveryFee')}</span>
                  <span className="font-semibold">€{deliveryFee.toFixed(2)}</span>
                </div>
              )}
              
              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>{t('discount')} ({discountPercent}%)</span>
                  <span className="font-semibold">-€{discount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="flex justify-between text-2xl font-bold text-black pt-2 border-t-2 border-gray-400">
                <span>{t('total')}</span>
                <span>€{finalTotal.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Minimum order warning for delivery */}
            {formData.orderType === 'delivery' && subtotal < 20 && (
              <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-500 rounded-lg">
                <p className="text-yellow-800 font-semibold text-sm">
                  {t('minOrderWarning').replace('{amount}', (20 - subtotal).toFixed(2))}
                </p>
              </div>
            )}
            
            {/* Info message about minimum order */}
            <div className="mt-4 p-3 bg-gray-100 border border-gray-300 rounded-lg">
              <p className="text-gray-700 text-sm">
                {t('minOrderDelivery')}
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-2 border-gray-300 hover:border-black font-semibold"
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={formData.orderType === 'delivery' && subtotal < 20}
              className="flex-1 bg-black text-white hover:bg-gray-800 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {t('placeOrder')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutModal;